import { Button, Input, Checkbox, Dropdown } from 'components/atoms/index';
import * as styles from 'components/organisms/Table/Table.css'
import { Icon } from '@/icons';
import { vars } from 'style/theme.css'
import Pagination from 'components/atoms/Pagination/Pagination';
import { useState, useEffect, useRef } from 'react';
import { Dialog } from '@/components/organisms/Modal/Dialog';
import { Modal } from '@/components/organisms/Modal/Modal';
import { SERVICE_TERMS_TEXT } from '@/constants/agreements';
import alpacaFinger from '@/assets/img/alpaca_finger.png';
import TaskCard from '@/components/organisms/Card/Exam/TaskCard';
import { academyPaperApi } from '@/services/api';
import { nanoid } from 'nanoid';
import { message as antMessage } from 'antd';
import { useSessionSlug } from '@/hooks/useSessionSlug';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    type ChartOptions,
    type ChartData,
  } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

interface ExamPaper {
    id: number
    title: string
    aiScoring: boolean
    questionCount: number
    price: number | null
    registrant?: string
    createdAt?: string
    sequence?: number
}

const data: ChartData<'bar'> = {
    labels: ['객관식', '주관식', '프리라이팅'],
    datasets: [
      {
        label: '점수',
        data: [85, 78, 100],
        backgroundColor: '#6366f1',
        borderRadius: { topLeft: 8, topRight: 8 },
        barThickness: 40,
      },
    ],
    
  };
  
  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
        padding: {
            top: 20,
        },
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      // ✅ datalabels 옵션은 plugins.datalabels 아래에
      datalabels: {
        anchor: 'end',
        align: 'top',
        offset: 2,
        font: { weight: 'normal' },
        color: '#6b7280',
        formatter: (v) => `${v}%`,
        display: true,
      },
    },
    scales: {
        y: {
          beginAtZero: true,
          max: 100, // 최대값을 120으로 설정 (막대 높이 제한)
          display: true,
          grid: {
            display: true,
            color: '#e5e7eb',
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 12,
            },
            stepSize: 50,
            callback: function(tickValue: string | number) {
              const value = Number(tickValue);
              return value === 0 || value === 50 || value === 100 ? value : '';
            },
          },
        },
        x: {
          display: true,
          grid: {
            display: false,
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 12,
            },
          },
        },
      },
  };
  
// 컬럼 렌더링용 메타 정보
const columns = [
    { key: 'sequence'     as const, label: '순번', render: (row: ExamPaper) => row.sequence ?? '-' },
    { key: 'title'   as const, label: '시험명', render: (row: ExamPaper) => row.title },
    { key: 'aiScoring' as const, label: '채점유형', render: (row: ExamPaper) => renderGradingType(row.aiScoring) },
    { key: 'questionCount' as const, render: (row: ExamPaper) => (
      <div className="flex gap-2 items-center lg:block">
        <span className="text-xs text-gray-400 lg:hidden">문항 수</span>
        <span>{row.questionCount}문항</span>
      </div>
    ) },
    { key: 'price' as const, render: (row: ExamPaper) => (
      <div className="flex gap-2 items-center lg:block">
        <span className="text-xs text-gray-400 lg:hidden">가격</span>
        <span>{row.price ? row.price.toLocaleString() + '원' : '-'}</span>
      </div>
    ) },
]

type ColumnKey = (typeof columns)[number]['key'];

const columnClassNameMap: Record<ColumnKey, string> = {
  sequence: 'max-w-[80px] !hidden lg:!flex justify-center',
  title: 'col-span-2 lg:col-span-1 font-bold',
  aiScoring: 'absolute top-5 right-5 lg:static max-w-[200px] lg:flex justify-center',
  questionCount: 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1',
  price: 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1',
};

const QUESTION_TYPE_OPTIONS = [
  { value: 'ALL', label: '전체 유형' },
  { value: 'MULTIPLE_CHOICE', label: '객관식' },
  { value: 'SHORT_ANSWER', label: '주관식' },
  { value: 'ESSAY', label: '서술형' },
  { value: 'TRUE_FALSE', label: 'OX' },
  { value: 'FILL_BLANK', label: '빈칸 채우기' },
  { value: 'MATCHING', label: '매칭' },
  { value: 'ORDERING', label: '순서 배열' },
] as const;

function renderGradingType(aiScoring: boolean) {
    if (aiScoring) {
        return <span className="font-medium text-blue-600">AI 채점</span>
    } else {
        return <span className="font-medium text-gray-600">일반 채점</span>
    }
}

// 시험지 리스트
export default function AcademyPaperList() {
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState<ExamPaper | null>(null);
  const [purchaseQuantity, setPurchaseQuantity] = useState(1);
  const [showTerms, setShowTerms] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [questionTypeSummary, setQuestionTypeSummary] = useState<{ type: string; label: string; count: number }[]>([]);
  const [questionTypeLoading, setQuestionTypeLoading] = useState(false);
  const [papers, setPapers] = useState<ExamPaper[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [questionTypeFilter, setQuestionTypeFilter] = useState<(typeof QUESTION_TYPE_OPTIONS)[number]['value']>('ALL');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(10);
  
  // 토스페이먼츠 관련 state
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showPaymentWidget, setShowPaymentWidget] = useState(false);
  const [widgetReady, setWidgetReady] = useState(false);
  const paymentWidgetRef = useRef<any>(null);
  const paymentMethodsWidgetRef = useRef<any>(null);
  const agreementWidgetRef = useRef<any>(null);
  // 결제 위젯 전용 클라이언트 키 사용 (API 키가 아님!)
  const clientKey = process.env.REACT_APP_TOSS_CLIENT_KEY || '';
  
  // 로그인된 학원 정보
  const { academyCode: sessionAcademyCode, academyName: sessionAcademyName } = useSessionSlug();
  const academyCode = sessionAcademyCode || '';
  const academyName = sessionAcademyName || '학원';
  const academyUserId = localStorage.getItem('academy_user_id') || '1';

  // 시험지 목록 조회
  const fetchPapers = async () => {
    try {
      setLoading(true);
      const resolvedQuestionType = questionTypeFilter === 'ALL' ? undefined : questionTypeFilter;
      console.log('시험지 목록 조회 시작:', { searchQuery, questionType: resolvedQuestionType, currentPage, pageSize });
      
      const response = await academyPaperApi.list({
        q: searchQuery,
        questionType: resolvedQuestionType,
        page: currentPage,
        size: pageSize
      });
      
      console.log('API 응답:', response);
      
      if (response && response.data) {
        const content = response.data.content || [];
        const totalCount = response.data.totalElements || content.length;
        const pageNumber = response.data.number ?? currentPage;
        const size = response.data.size ?? pageSize;
        const mapped = content.map((paper: ExamPaper, idx: number) => ({
          ...paper,
          sequence: totalCount - (pageNumber * size + idx),
        }));
        setPapers(mapped);
        setTotalElements(totalCount);
        console.log('시험지 데이터 설정 완료:', content.length, '개');
      } else {
        console.warn('응답 데이터가 없습니다:', response);
        setPapers([]);
        setTotalElements(0);
      }
    } catch (error: any) {
      console.error('시험지 목록 조회 실패:', error);
      console.error('에러 상세:', error?.response?.data);
      setPapers([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPapers();
  }, [currentPage, questionTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // 페이지 로드 시 토스페이먼츠 위젯 초기화
  useEffect(() => {
    const initTossPayments = async () => {
      try {
        console.log('🎯 토스페이먼츠 SDK 초기화 시작');
        
        const TossPayments = (window as any).TossPayments;
        if (!TossPayments) {
          console.error('TossPayments SDK가 로드되지 않았습니다.');
          return;
        }

        const customerKey = `${academyCode}_${academyUserId}_${Date.now()}`;
        console.log('✅ customerKey 생성:', customerKey);
        
        const tossPayments = TossPayments(clientKey);
        const widgets = tossPayments.widgets({ customerKey });
        
        paymentWidgetRef.current = widgets;
        setWidgetReady(true);
        
        console.log('✅ 토스페이먼츠 위젯 준비 완료');
      } catch (error) {
        console.error('❌ 토스페이먼츠 초기화 실패:', error);
      }
    };

    if (clientKey && !widgetReady) {
      initTossPayments();
    }
  }, [clientKey, academyCode, academyUserId, widgetReady]);

  // 결제 위젯 표시 상태가 변경되면 위젯 렌더링 (이미 렌더된 경우 재렌더링 금지)
  useEffect(() => {
    if (showPaymentWidget && selectedPaper && widgetReady && paymentWidgetRef.current && !paymentMethodsWidgetRef.current) {
      console.log('📌 useEffect 트리거: 결제 위젯 렌더링 예약');
      const timer = setTimeout(() => {
        console.log('⏰ 타이머 실행: 결제 위젯 렌더링 시작');
        renderPaymentWidget();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showPaymentWidget, widgetReady, selectedPaper]); // eslint-disable-line react-hooks/exhaustive-deps

  // 수량/금액 변경 시 위젯 금액만 업데이트
  useEffect(() => {
    if (showPaymentWidget && selectedPaper && widgetReady && paymentWidgetRef.current && paymentMethodsWidgetRef.current) {
      const totalAmount = (selectedPaper.price || 0) * purchaseQuantity;
      console.log('🔁 setAmount 업데이트:', totalAmount);
      paymentWidgetRef.current
        .setAmount({ currency: 'KRW', value: totalAmount })
        .catch((e: any) => console.error('setAmount 업데이트 실패', e));
    }
  }, [purchaseQuantity, selectedPaper, showPaymentWidget, widgetReady]);

  // 위젯 숨김 시 정리
  useEffect(() => {
    if (!showPaymentWidget) {
      cleanupPaymentWidgets();
    }
  }, [showPaymentWidget]);

  const handleRowClick = async (paper: ExamPaper) => {
    const unitPrice = typeof paper.price === 'number' ? paper.price : 0;
    if (unitPrice < 200) {
      antMessage.warning('가격이 200원 미만인 시험지는 결제할 수 없습니다. 관리자에게 문의해주세요.');
      return;
    }

    setSelectedPaper(paper);
    setPurchaseQuantity(1);
    setShowPurchaseDialog(true);
    
    setQuestionTypeSummary([]);
    setQuestionTypeLoading(true);
    try {
      const detailResponse = await academyPaperApi.detail(paper.id);
      const detail = detailResponse?.data || detailResponse;
      const items = Array.isArray(detail?.items) ? detail.items : [];
      if (items.length > 0) {
        const counts: Record<string, number> = {};
        items.forEach((item: any) => {
          const type = (item?.questionType || 'UNKNOWN').toUpperCase();
          counts[type] = (counts[type] || 0) + 1;
        });
        const labelMap: Record<string, string> = {
          MULTIPLE_CHOICE: '객관식',
          SHORT_ANSWER: '주관식',
          ESSAY: '서술형',
          TRUE_FALSE: 'OX',
          MATCHING: '매칭',
        };
        const summary = Object.entries(counts).map(([type, count]) => ({
          type,
          label: labelMap[type] || type,
          count,
        })).sort((a, b) => b.count - a.count);
        setQuestionTypeSummary(summary);
      } else {
        setQuestionTypeSummary([]);
      }
    } catch (error) {
      console.error('문항 유형 조회 실패:', error);
      setQuestionTypeSummary([]);
    } finally {
      setQuestionTypeLoading(false);
    }
  };

  const handleQuestionTypeChange = (value: string) => {
    setQuestionTypeFilter(value as (typeof QUESTION_TYPE_OPTIONS)[number]['value']);
    setCurrentPage(0);
  };

  const handlePreviewPaper = () => {
    setShowPreviewDialog(true);
  };

  const handleClosePreviewDialog = () => {
    setShowPreviewDialog(false);
  };

  const handleClosePurchaseDialog = () => {
    setShowPurchaseDialog(false);
    setSelectedPaper(null);
    setQuestionTypeSummary([]);
    setQuestionTypeLoading(false);
    setPurchaseQuantity(1);
    setAgreedToTerms(false);
  };

  const handleNextStep = () => {
    if (!agreedToTerms) {
      Modal.alert({ message: '서비스 이용약관에 동의해주세요.' });
      return;
    }
    handleConfirmPurchase();
  };

  // 결제 위젯 렌더링 (샘플 코드 방식)
  const renderPaymentWidget = async () => {
    console.log('🔍 renderPaymentWidget 호출됨');
    console.log('  - selectedPaper:', !!selectedPaper);
    console.log('  - paymentWidgetRef.current:', !!paymentWidgetRef.current);
    
    if (!selectedPaper || !paymentWidgetRef.current) {
      console.error('❌ selectedPaper 또는 paymentWidgetRef가 없습니다', {
        selectedPaper: !!selectedPaper,
        paymentWidgetRef: !!paymentWidgetRef.current
      });
      return;
    }
    
    setPaymentLoading(true);
    console.log('🎨 결제 위젯 렌더링 함수 시작');
    
    try {
      // DOM 요소 확인
      const paymentMethodEl = document.querySelector('#payment-method');
      const agreementEl = document.querySelector('#agreement');
      
      console.log('🔍 DOM 요소 확인:', {
        paymentMethod: !!paymentMethodEl,
        agreement: !!agreementEl
      });
      
      if (!paymentMethodEl || !agreementEl) {
        console.error('❌ DOM 요소가 없습니다!');
        antMessage.error('결제 화면 요소를 찾을 수 없습니다.');
        setPaymentLoading(false);
        return;
      }

      // 기존 UI가 있으면 제거
      if (paymentMethodsWidgetRef.current) {
        try {
          await paymentMethodsWidgetRef.current.destroy();
        } catch (e) {
          console.warn('paymentMethodsWidget destroy 실패', e);
        }
        paymentMethodsWidgetRef.current = null;
      }
      if (agreementWidgetRef.current) {
        try {
          await agreementWidgetRef.current.destroy();
        } catch (e) {
          console.warn('agreementWidget destroy 실패', e);
        }
        agreementWidgetRef.current = null;
      }

      const widgets = paymentWidgetRef.current;
      const totalAmount = (selectedPaper.price || 0) * purchaseQuantity;
      
      console.log('💰 결제 금액 설정:', totalAmount);
      
      // 1. 결제 금액 설정
      await widgets.setAmount({
        currency: 'KRW',
        value: totalAmount,
      });
      
      console.log('✅ setAmount 완료');
      console.log('🎨 UI 렌더링 시작 (각각 렌더링)');
      
      // 2. 결제 UI 렌더링
      console.log('📝 renderPaymentMethods 시작...');
      const paymentMethodWidget = await widgets.renderPaymentMethods({
        selector: '#payment-method',
        variantKey: 'DEFAULT',
      });
      paymentMethodsWidgetRef.current = paymentMethodWidget;
      console.log('✅ renderPaymentMethods 완료');
      
      // 3. 약관 UI 렌더링
      console.log('📝 renderAgreement 시작...');
      const agreementWidget = await widgets.renderAgreement({
        selector: '#agreement',
        variantKey: 'AGREEMENT'
      });
      agreementWidgetRef.current = agreementWidget;
      console.log('✅ renderAgreement 완료');
      
      console.log('✅ 결제 위젯 렌더링 완료');
      setPaymentLoading(false);
    } catch (error) {
      console.error('❌ 결제 위젯 렌더링 실패:', error);
      const err = error as any;
      console.error('에러 타입:', err?.constructor?.name);
      console.error('에러 메시지:', err?.message);
      console.error('에러 스택:', err?.stack);
      antMessage.error('결제 화면을 준비하는 중 오류가 발생했습니다.');
      setPaymentLoading(false);
      setShowPaymentWidget(false);
    }
  };

  const cleanupPaymentWidgets = async () => {
    try {
      if (paymentMethodsWidgetRef.current) {
        await paymentMethodsWidgetRef.current.destroy();
        paymentMethodsWidgetRef.current = null;
      }
      if (agreementWidgetRef.current) {
        await agreementWidgetRef.current.destroy();
        agreementWidgetRef.current = null;
      }
    } catch (e) {
      console.warn('결제 위젯 cleanup 실패', e);
    }
  };

  const handleConfirmPurchase = () => {
    if (!selectedPaper) return;
    
    // 약관 동의 확인
    if (!agreedToTerms) {
      antMessage.warning('서비스 이용약관에 동의해주세요.');
      return;
    }
    
    // 먼저 UI를 전환하고, useEffect에서 위젯 초기화
    setShowPaymentWidget(true);
  };

  // 실제 결제 요청
  const handlePaymentRequest = async () => {
    if (!selectedPaper || !paymentWidgetRef.current) return;

    const orderId = `paper${selectedPaper.id}_qty${purchaseQuantity}_acd${academyCode}_${nanoid()}`;
    const academyEmail = `${academyCode}@academy.com`; // 학원 이메일 (실제 이메일이 없는 경우)

    try {
      console.log('결제 요청 시작:', {
        orderId,
        paperTitle: selectedPaper.title,
        quantity: purchaseQuantity,
        academyName,
        academyEmail,
        academyUserId,
      });
      
      // V2 API: requestPayment 파라미터 형식
      await paymentWidgetRef.current.requestPayment({
        orderId: orderId,
        orderName: `${selectedPaper.title} (x${purchaseQuantity})`,
        successUrl: `${window.location.origin}/academy/paper/payment/success?paperId=${selectedPaper.id}&quantity=${purchaseQuantity}&academyCode=${academyCode}`,
        failUrl: `${window.location.origin}/academy/paper/payment/fail`,
        customerEmail: academyEmail,
        customerName: academyName,
      });
    } catch (error) {
      console.error('결제 요청 실패:', error);
      antMessage.error('결제 요청에 실패했습니다.');
    }
  };

  const handleQuantityChange = (delta: number) => {
    setPurchaseQuantity(prev => Math.max(1, prev + delta));
  };

  const handleSearch = () => {
    if (currentPage === 0) {
      fetchPapers();
    } else {
      setCurrentPage(0);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page - 1); // Pagination 컴포넌트는 1부터 시작
  };

  return (
    <main className='flex flex-1 justify-start pt-8 w-full bg-white md:bg-transparent'>
      <div className='flex flex-col w-full'>
        <div className='flex justify-between items-center'>
          <h1 className='heading1'>시험지 관리</h1>
        </div>

        <section className='flex flex-1 w-full lg:px-0'>
          <div className='flex flex-col w-full'>
            <section className='flex flex-col gap-2 items-start pt-3 w-full lg:flex-row lg:justify-between lg:items-center'>
              <div className='flex flex-col gap-2 w-full lg:flex-row lg:items-center'>
                <Input
                  placeholder='검색어를 입력해주세요.'
                  className='w-full lg:max-w-[420px]'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  icon={<Icon name="search" size={20} color={vars.color.gray[300]} />}
                />
                <div className='flex flex-col w-full max-w-[220px] gap-1'>
                  <span className='text-xs font-semibold text-gray-500'>시험지 유형</span>
                  <Dropdown
                    className='w-full'
                    options={QUESTION_TYPE_OPTIONS.map((option) => ({
                      value: option.value,
                      label: option.label,
                    }))}
                    value={questionTypeFilter}
                    onChange={handleQuestionTypeChange}
                    placeholder='시험지 유형 선택'
                  />
                </div>
              </div>
              <Button onClick={handlePreviewPaper}>
                시험지 샘플 미리보기
              </Button>
            </section>

            <section className='flex flex-col flex-1 justify-between pt-5 w-full'>
              {loading ? (
                <div className='flex justify-center items-center py-20'>
                  <p className='text-gray-500'>로딩 중...</p>
                </div>
              ) : papers.length === 0 ? (
                <div className='flex justify-center items-center py-20'>
                  <p className='text-gray-500'>등록된 시험지가 없습니다.</p>
                </div>
              ) : (
                <>
                  <div className='w-full'>
                    <ul className={`${styles.academyHeaderList} w-full`}>
                      <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
                      <li className={styles.headerItem}>시험명</li>
                      <li className={`${styles.headerItem} max-w-[200px] lg:flex justify-center`}>채점유형</li>
                      <li className={`${styles.headerItem} max-w-[200px]`}>문항 수</li>
                      <li className={`${styles.headerItem} max-w-[200px]`}>가격</li>
                    </ul>
                    <div className='flex flex-col gap-4 w-full lg:gap-0'>
                      {papers.map((row) => (
                        <ul
                          key={row.id}
                          className={`${styles.academyBodyList} lg:shadow-none table-card cursor-pointer hover:bg-gray-50 transition-colors !grid grid-cols-2 lg:!flex`}
                          onClick={() => handleRowClick(row)}
                        >
                          {columns.map(({ key, render }) => (
                            <li
                              key={key}
                              className={`${styles.bodyItem} ${columnClassNameMap[key]}`}
                            >
                              {render(row)}
                            </li>
                          ))}
                        </ul>
                      ))}
                    </div>
                  </div>
                  <div className='py-5'>
                    <Pagination
                      total={totalElements}
                      pageSize={pageSize}
                      current={currentPage + 1}
                      onChange={handlePageChange}
                    />
                  </div>
                </>
              )}
            </section>
          </div>
        </section>

        {showPreviewDialog && (
          <Dialog
            onOk={handleClosePreviewDialog}
            onCancel={handleClosePreviewDialog}
            okText="확인"
            className='!max-w-[1000px]'
          >
            <div className="p-6">
              <h3 className="mb-4 text-lg font-semibold">시험지 미리보기</h3>

              <section className='flex relative gap-2 items-end mb-5'>
                <div className='relative h-[140px] w-[120px] flex-none overflow-hidden'>
                  <img src={alpacaFinger} alt="" className='absolute left-0 -bottom-6 w-full' />
                </div>
                <p className='px-5 py-3 rounded-2xl card'>
                  알파카가 자체 개발한 커리큘럼과 학습 데이터를 바탕으로 구성한 프리미엄 시험지이며,객관식, 주관식, 프리라이팅 등 다양한 유형으로 구성되어 있어, 목적에 맞는 맞춤형 평가가 가능합니다.<br />
                  학생이 시험에 응시하면, 알파카 AI 시스템을 통해 채점을 진행하며 체계적인 성적표를 제공합니다.
                </p>
              </section>

              <section>
                <div className='overflow-hidden rounded-2xl border border-gray-200'>
                  <h2 className='p-3 text-lg font-bold back_gra_ravendar'>1.객관식, 주관식 유형으로 구성된 다양한 문항</h2>
                  <div className='grid grid-cols-2  p-3 *:py-10  *:px-5 *:odd:border-r *:border-gray-200'>
                    <TaskCard 
                      questionType='multiple'
                      question='1. They _____ the game together.'
                      options={[
                        { id: 1, word: 'play' },
                        { id: 2, word: 'plays' },
                        { id: 3, word: 'played' },
                        { id: 4, word: 'playing' },
                      ]}
                      hasImage={false}
                      readOnly
                    />
                    <TaskCard 
                      questionType='multiple'
                      question='2. Choose the correct sentence'
                      options={[
                        { id: 1, word: 'The cat watch the window.' },
                        { id: 2, word: 'The cat watches the window.' },
                        { id: 3, word: 'The cats watching the window.' },
                        { id: 4, word: 'The cat is watch the window.' },
                      ]}
                      hasImage={false}
                      readOnly
                    />
                    <TaskCard 
                      questionType='multiple'
                      question='3. Choose the incorrect sentence'
                      options={[
                        { id: 1, word: 'He eats bread.' },
                        { id: 2, word: 'I walks to school.' },
                        { id: 3, word: 'She has a dog.' },
                        { id: 4, word: 'They play outside.' },
                      ]}
                      hasImage={false}
                      readOnly
                    />
                    <TaskCard 
                      questionType='short'
                      question='4. “play” “every day” , “soccer” , “He” Unscramble the sentence.'
                      showAnswer={false}
                      showFeedback={false}
                      hasImage={false}
                      isExamMode={true}
                      readOnly
                    />
                    <TaskCard 
                      questionType='short'
                      question='5. “나는 사과를 좋아한다.” Translate into English '
                      showAnswer={false}
                      showFeedback={false}
                      hasImage={false}
                      isExamMode={true}
                      readOnly
                    />
                    <TaskCard 
                      questionType='essay'
                      question='6. 본인에게 인상 깊었던 여행지의 경험을 영작하세요.'
                      showAnswer={false}
                      showFeedback={false}
                      hasImage={false}
                      isExamMode={true}
                      readOnly
                    />
                  </div>
                </div>

                <div className='overflow-hidden mt-5 rounded-2xl border border-gray-200'>
                  <h2 className='p-3 text-lg font-bold back_gra_ravendar'>1.객관식, 주관식 유형으로 구성된 다양한 문항</h2>
                  <div className='flex flex-col gap-5'>
                    <div className='flex flex-col gap-5 p-5'>
                      <section className='overflow-hidden rounded-2xl'>
                        <div className='flex justify-between items-center px-5 py-3 text-lg font-bold back_gra_ravendar'>
                          <h2>중간고사-1-1</h2>
                          <p>(90/100)</p>
                        </div>
                        <div className='p-5 bg-gray-50'>
                          <p>홍길동 학생의 객관식 항목 점수는 전체적으로 우수한  편입니다만, 일부 문제에서 자주 틀리는 유형이 발견되었습니다.
                          특히 7번, 9번, 11번 문제는 동일한 유형의 문법에 대해 적절한 단어를 고르는 유형이나, 이러한 문법 유형에 취약한 것으로 확인됩니다.</p>
                        </div>
                      </section>
                      <section className='overflow-hidden rounded-2xl'>
                        <div className='flex justify-between items-center px-5 py-3 text-lg font-bold back_gra_ravendar'>
                          <h2>주관식 시험</h2>
                          <p>(90/100)</p>
                        </div>
                        <div className='p-5 bg-gray-50'>
                          <p>주어진 문장에 대비 작성능력이 우수한 편입니다. 다만, 답안 작성시 띄어쓰기와 오타 등을 더욱 주의할 수 있도록 하면 좋겠습니다.</p>
                        </div>
                      </section>
                      <section className='overflow-hidden rounded-2xl'>
                        <div className='flex justify-between items-center px-5 py-3 text-lg font-bold back_gra_ravendar'>
                          <h2>프리라이팅 시험</h2>
                          <p>(90/100)</p>
                        </div>
                        <div className='p-5 bg-gray-50'>
                          <p>구사하는 문장 대부분에 큰 오류는 없으나, 일부 고난도 문법에 취약한 부분이 확인됩니다.</p>
                        </div>
                      </section>
                    </div>
                  </div>
                </div>

                <section className=''>
                  <h2 className='pt-8 pb-5 heading1'>시험 성적 요약</h2>
                  <div className='lg:grid lg:grid-cols-2 lg:gap-10'>
                    <div>
                      <div className='flex flex-col gap-2'>
                        <span className='text-sm font-bold text-gray-500'>시험 점수</span>
                        <div className='overflow-hidden rounded-2xl border border-gray-200'>
                          <table className='w-full text-sm font-bold text-center'>
                            <colgroup>
                              <col className='text-center' />
                              <col className='w-[calc((100%/3)-50px)]' />
                              <col className='w-[calc((100%/3)-50px)]' />
                              <col className='w-[calc((100%/3)-50px)]' />
                            </colgroup>
                            <thead className='text-gray-500 bg-gray-50'>
                              <tr className='*:py-1.5'>
                                <th></th>
                                <th>객관식</th>
                                <th>주관식</th>
                                <th>프리라이팅</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className='*:py-1'>
                                <td>총점</td>
                                <td>70</td>
                                <td>20</td>
                                <td>10</td>
                              </tr>
                              <tr className='*:py-1'>
                                <td>득점</td>
                                <td className='text-indigo-500'>50</td>
                                <td className='text-indigo-500'>20</td>
                                <td className='text-indigo-500'>10</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className='flex flex-col gap-2 mt-4'>
                        <span className='text-sm font-bold text-gray-500'>감점된 문제</span>
                        <div className='overflow-hidden p-4 bg-gray-50 rounded-2xl border border-gray-200'>
                          <ul className='flex flex-col gap-2'>
                            {Array.from({ length: 5 }, (_, index) => (
                              <li key={index + 1} className='flex justify-between items-center p-2 bg-white rounded-lg'>
                                <p className='underline'>{index + 1}번</p>
                                <Button size='small'>선생님 코멘트 보기</Button>  
                              </li>
                            ))}       
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div>
                      <span className='text-sm font-bold text-gray-500'>시험 요약</span>
                      <div className='p-4 bg-gray-50 rounded-2xl border border-gray-200'>
                        <div className='pt-8 pb-4 h-100'>
                          <Bar data={data} options={options} className='overflow-visible' />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <div className='p-5 mt-5 space-y-2 text-sm text-gray-600 bg-gray-50 rounded-2xl border border-gray-200'>
                  <h1 className='text-sm font-bold'>유의사항</h1>
                  <p>1. 유료 시험은 서술형 문제가 있을 수 있으므로 PC와, 태블릿에서 응시하시는 것을 권장합니다.</p>
                  <p>2. 선다형 문제는 클릭, 탭 등으로 선택이 가능합니다.</p>
                  <p>3. 정답이 정해진 주관식 서술형 문제는 오타, 공백 등을 고려하여 평가가 진행됩니다. 시험 채점은 알파카에서 진행하며, 프리라이팅 부문은 AI 를 통해 채점합니다.</p>
                  <p>4. 시험 응시도중 탭을 전환할 경우 경고 처리 됩니다. 3 회 경고 누적시 자동으로 제출처리 되며, 재응시나 환불이 불가합니다.</p>
                  <p>5. 가이드 페이지에서 제공된 문제는 실제 알파영어 교재상에서 제공되는 문제는 아니며, 이해를 돕기 위한 예시문제입니다.</p>
                  <p>6.시험지 유효기간은 구매일로부터 1년이며, 1년이내 미응시할 경우 응시권은 자동 소멸됩니다.</p>
                </div>
              </section>
            </div>
          </Dialog>
        )}

        {showPurchaseDialog && selectedPaper && (
          <Dialog
            hideFooter
            className={showPaymentWidget ? '!max-w-[720px]' : undefined}
            onCancel={() => {
              handleClosePurchaseDialog();
              setShowPaymentWidget(false);
              setPaymentLoading(false);
              cleanupPaymentWidgets();
            }}
          >
            <div className="p-6">
              <h3 className="mb-4 text-lg font-semibold">시험지 구매</h3>
              {!showPaymentWidget ? (
                <div className="space-y-6 text-center">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">{selectedPaper.title}</h4>
                    <p className="mt-1 text-sm text-gray-500">
                      {selectedPaper.aiScoring ? 'AI 채점' : '일반 채점'}
                    </p>
                  </div>
                  <div className='mx-auto flex items-center justify-center rounded-2xl border border-gray-200 w-full max-w-[200px] overflow-hidden'>
                    <button 
                      onClick={() => handleQuantityChange(-1)}
                      className='flex-1 py-3 text-2xl font-semibold text-indigo-500 hover:bg-gray-50'
                    >
                      -
                    </button>
                    <input 
                      type="number" 
                      value={purchaseQuantity}
                      onChange={(e) => setPurchaseQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className='w-20 text-2xl font-bold text-center text-gray-900 border-none focus:outline-none'
                    />
                    <button 
                      onClick={() => handleQuantityChange(1)}
                      className='flex-1 py-3 text-2xl font-semibold text-indigo-500 hover:bg-gray-50'
                    >
                      +
                    </button>
                  </div>
                  <p className='text-sm font-semibold text-indigo-500'>
                    {questionTypeLoading
                      ? '문항 정보를 불러오는 중입니다...'
                      : questionTypeSummary.length > 0
                        ? `${questionTypeSummary.map((summary) => summary.label).join(', ')} 부문 포함`
                        : '문항 정보를 확인할 수 없습니다.'}
                  </p>
                  <div className='space-y-1'>
                    <p className='text-sm font-semibold text-gray-500'>합계</p>
                    <p className='text-3xl font-bold text-gray-900'>
                      {selectedPaper.price 
                        ? (selectedPaper.price * purchaseQuantity).toLocaleString() + '원' 
                        : '-'}
                    </p>
                  </div>
                  <div className='flex flex-col gap-2 items-center text-sm text-gray-500'>
                    <Checkbox
                      label="서비스 이용약관"
                      checked={agreedToTerms}
                      onChange={setAgreedToTerms}
                    />
                    <button type='button' onClick={() => setShowTerms(true)} className='text-xs text-gray-400 underline'>
                      이용약관 보기
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="p-4 mb-4 bg-gray-50 rounded-lg">
                    <h4 className="mb-2 text-xl font-bold">{selectedPaper.title}</h4>
                    <div className="text-sm text-gray-600">
                      <p>수량: {purchaseQuantity}개</p>
                      <p className="mt-2 text-lg font-bold text-indigo-500">
                        총 결제금액: {((selectedPaper.price || 0) * purchaseQuantity).toLocaleString()}원
                      </p>
                    </div>
                  </div>
                  <div className="relative">
                    {paymentLoading && (
                      <div className="flex absolute inset-0 z-10 justify-center items-center bg-white/60">
                        <p>결제 준비 중...</p>
                      </div>
                    )}
                    <div id="payment-method" style={{ minHeight: 280 }}></div>
                    <div id="agreement" className="mt-4"></div>
                  </div>
                </div>
              )}
              <div className='flex gap-2 justify-center items-center pt-6 mt-6 border-t border-gray-200'>
                <Button
                  intent="secondary"
                  className="w-full max-w-[280px]"
                  onClick={() => {
                    handleClosePurchaseDialog();
                    setShowPaymentWidget(false);
                    setPaymentLoading(false);
                    cleanupPaymentWidgets();
                  }}
                >
                  취소
                </Button>
                <Button
                  className="w-full max-w-[280px]"
                  onClick={showPaymentWidget ? handlePaymentRequest : handleNextStep}
                >
                  {showPaymentWidget ? '결제하기' : '다음'}
                </Button>
              </div>
            </div>
          </Dialog>
        )}

        <Dialog
          open={showTerms}
          okText="확인"
          cancelText="닫기"
          onOk={() => setShowTerms(false)}
          onCancel={() => setShowTerms(false)}
          className="max-w-3xl"
        >
          <div className='p-5 space-y-4 max-h-[60vh] overflow-auto'>
            <h3 className='text-xl font-bold text-gray-900'>서비스 이용약관</h3>
            <pre className='text-sm text-gray-700 whitespace-pre-wrap'>
              {SERVICE_TERMS_TEXT}
            </pre>
          </div>
        </Dialog>
      </div>
    </main>
  );
}